import * as React from 'react'
import { useContext, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import debounce from 'lodash/debounce'
import moment from 'moment'

import AutoCompleteSearchField from 'modules/core/components/AutoCompleteSearchField'
import DayPicker from 'modules/core/components/DayPicker'
import { GoogleMapsContext } from 'modules/core/components/GoogleMaps/GoogleMapsProvider'
import Select from 'modules/core/components/Select'
import { useCallback, useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import FormBlock from 'modules/expenses/components/expenses/Detail/_newForm/Sections/FormBlock'
import { Expensable } from 'modules/expenses/types/Expensable'

// Generate time options (same as wizard)
const generateTimeOptions = () => {
  return [...Array(24)].flatMap((_, hour) =>
    [0, 30].map((minute) => ({
      label: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
      value: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
    }))
  )
}

const timeOptions = generateTimeOptions()

interface Suggestion {
  label: string
  value: string
}

interface LocationData {
  country: string
  administrativeAreaLevel1?: string
  administrativeAreaLevel2?: string
  locality?: string
}

interface Props {
  expensable?: Expensable
}

const VerticalConnector = () => (
  <Box paddingLeft="s4" height="s12">
    <Box width="s2" height="s12" background="grey200" />
  </Box>
)

interface DestinationItemProps {
  locationName: string
  arrivalDate?: string
  showArrivalDate: boolean
  onLocationChange: (value: string) => void
  onLocationSelect: (
    result: { value: string; label: string },
    locationData: LocationData | null
  ) => void
  onDateChange?: (dateString: string) => void
  onTimeChange?: (timeString?: string) => void
  minDate?: string | null
  placesLibraryLoaded: boolean
}

const DestinationItem = ({
  locationName,
  arrivalDate,
  showArrivalDate,
  onLocationChange,
  onLocationSelect,
  onDateChange,
  onTimeChange,
  minDate,
  placesLibraryLoaded,
}: DestinationItemProps) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [searchValue, setSearchValue] = useState<string>('')

  const placesContainerRef = useRef<HTMLDivElement>(null)

  const requestIdRef = useRef(0)

  const debouncedSearchRef = useRef(
    debounce((val: string, setter: React.Dispatch<React.SetStateAction<string>>) => {
      setter(val)
    }, 500)
  )

  useEffect(() => {
    const debouncedFn = debouncedSearchRef.current
    return () => {
      debouncedFn.cancel()
    }
  }, [])

  useEffect(() => {
    if (typeof google === 'undefined' || !placesLibraryLoaded) return

    if (searchValue.length === 0) {
      setSuggestions([])
      return
    }

    const currentRequestId = ++requestIdRef.current

    new google.maps.places.AutocompleteService().getPlacePredictions(
      { input: searchValue, language: i18n.locale || 'en' },
      (placeSuggestions) => {
        if (currentRequestId !== requestIdRef.current) return

        setSuggestions(
          placeSuggestions?.map((suggestion) => ({
            label: suggestion.description,
            value: suggestion.place_id,
          })) || []
        )
      }
    )
  }, [searchValue, placesLibraryLoaded])

  const handleSearch = useCallback((value: string) => {
    debouncedSearchRef.current(value, setSearchValue)
  }, [])

  const clearSuggestions = useCallback(() => {
    setSuggestions([])
  }, [])

  const getPlaceDetails = useUnsafeCallback(
    (placeId: string): Promise<LocationData | null> => {
      return new Promise((resolve) => {
        if (!placesContainerRef.current || typeof google === 'undefined' || !placesLibraryLoaded) {
          resolve(null)
          return
        }

        const request = {
          placeId,
          fields: ['geometry', 'address_components', 'name'],
          language: i18n.locale || 'en',
        }
        const placeService = new google.maps.places.PlacesService(placesContainerRef.current)

        placeService.getDetails(request, (result, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && result) {
            let country = ''
            let administrativeAreaLevel1 = ''
            let administrativeAreaLevel2 = ''
            let locality = ''

            for (const component of result.address_components ?? []) {
              const componentType = component.types[0]
              switch (componentType) {
                case 'country':
                  country = component.short_name.toUpperCase()
                  break
                case 'administrative_area_level_1':
                  administrativeAreaLevel1 = component.long_name
                  break
                case 'administrative_area_level_2':
                  administrativeAreaLevel2 = component.long_name
                  break
                case 'locality':
                  locality = component.long_name
                  break
              }
            }

            resolve({
              country,
              administrativeAreaLevel1: administrativeAreaLevel1 || undefined,
              administrativeAreaLevel2: administrativeAreaLevel2 || undefined,
              locality: locality || undefined,
            })
          } else {
            resolve(null)
          }
        })
      })
    },
    [placesLibraryLoaded]
  )

  const dateComponent = arrivalDate ? i18nDateToString(moment(arrivalDate), 'ui') : ''
  const time = arrivalDate ? moment(arrivalDate).format('HH:mm') : undefined

  const handleChange = (value: string) => {
    onLocationChange(value)
    handleSearch(value)
  }

  const handleSelect = async (result: { value: string; label: string }) => {
    const locationData = await getPlaceDetails(result.value)
    onLocationSelect(result, locationData)
    clearSuggestions()
  }

  return (
    <Box flexDirection="row" gap="s8" alignItems="center">
      <div ref={placesContainerRef} style={{ display: 'none' }} />
      <Box width="s8" height="s8" background="radicalRed600" borderRadius={{ all: 'abs100' }} />
      <Box flexDirection="column" gap="s4" flexGrow>
        <Text.Normal size="200" weight="medium" color="grey1000">
          {i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.destination')}
        </Text.Normal>
        <AutoCompleteSearchField
          value={locationName}
          onChange={handleChange}
          onSelect={handleSelect}
          placeholder={i18n.t(
            'expenses_v2.forms.germany_per_diem_modal.trip_details.destination_placeholder'
          )}
          icon="pin"
          suggestions={suggestions}
        />
      </Box>
      {showArrivalDate && (
        <>
          <Box flexDirection="column" gap="s4" flexNoShrink>
            <Text.Normal size="200" weight="medium" color="grey1000">
              {i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.arrival')}
            </Text.Normal>
            <DayPicker
              mode="single"
              placeholder="--/--/----"
              minDate={minDate ? moment(minDate) : undefined}
              onChange={onDateChange || (() => {})}
              value={dateComponent}
              focusTrapActive={false}
            />
          </Box>
          <Box flexDirection="column" gap="s4" flexNoShrink>
            <Text.Normal size="200" weight="medium" color="grey1000">
              {i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.time')}
            </Text.Normal>
            <Box width="s120">
              <Select
                none="--:--"
                readonly={!arrivalDate}
                options={timeOptions}
                value={time}
                onChange={onTimeChange}
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  )
}

interface DateTimeFieldProps {
  label: string
  date: string | null
  helperText?: string
  fieldName: string
  minDate?: string | null
}

const DateTimeField = ({ label, date, helperText, fieldName, minDate }: DateTimeFieldProps) => {
  const { setValue, watch } = useFormContext()
  // Use RHF as single source of truth
  // Only fall back to `date` prop on initial render when RHF hasn't been set yet
  const watchedValue = watch(fieldName)
  const currentValue = watchedValue !== undefined ? watchedValue : date

  const dateComponent = currentValue ? i18nDateToString(moment(currentValue), 'ui') : ''
  const time = currentValue ? moment(currentValue).format('HH:mm') : undefined

  const handleDateChange = (dateString: string) => {
    if (!dateString) return

    const newDate = stringToDate(dateString, 'ui')
    if (currentValue) {
      newDate.set({
        hours: moment(currentValue).hours(),
        minutes: moment(currentValue).minutes(),
      })
    } else {
      newDate.set({ hours: 9, minutes: 0 })
    }
    setValue(fieldName, newDate.toISOString(), { shouldDirty: true })
  }

  const handleTimeChange = (timeString?: string) => {
    if (!timeString) return

    const [hours, minutes] = timeString.split(':').map(Number)
    // Use minDate as fallback instead of "now" if no currentValue
    const baseDate = currentValue ? moment(currentValue) : minDate ? moment(minDate) : moment()
    baseDate.set({ hours, minutes })
    setValue(fieldName, baseDate.toISOString(), { shouldDirty: true })
  }

  return (
    <Box flexDirection="column" gap="s8">
      <Box flexDirection="row" alignItems="center" gap="s8">
        <Box width="s8" height="s8" background="grey400" borderRadius={{ all: 'abs100' }} />
        <Text.Normal size="200" weight="medium" color="grey1000">
          {label}
        </Text.Normal>
      </Box>
      <Box paddingLeft="s16" flexDirection="row" gap="s8">
        <Box flexGrow>
          <DayPicker
            mode="single"
            placeholder="--/--/----"
            minDate={minDate ? moment(minDate) : undefined}
            onChange={handleDateChange}
            value={dateComponent}
            focusTrapActive={false}
          />
        </Box>
        <Box width="s120">
          <Select
            none="--:--"
            readonly={!currentValue}
            options={timeOptions}
            value={time}
            onChange={handleTimeChange}
          />
        </Box>
      </Box>
      {helperText && (
        <Box paddingLeft="s16">
          <Text.Normal size="100" color="grey600">
            {helperText}
          </Text.Normal>
        </Box>
      )}
    </Box>
  )
}

const Destinations = (props: Props) => {
  const { expensable } = props
  const { setValue, watch } = useFormContext()
  const { placesLibraryLoaded } = useContext(GoogleMapsContext)

  const perDiem = expensable?.perDiem

  // Watch form values - use expensable as fallback for initial values
  const watchedDestinations = watch('perDiem.perDiemDestinations')
  const destinations = watchedDestinations || perDiem?.perDiemDestinations || []

  const startDate = watch('perDiem.startDate') || perDiem?.startDate
  const endDate = watch('perDiem.endDate') || perDiem?.endDate

  // Calculate trip duration for summary (must be before early return)
  const tripDuration = useMemo(() => {
    if (!startDate || !endDate) {
      return null
    }
    const start = moment(startDate)
    const end = moment(endDate)
    const days = end.diff(start, 'days') + 1
    const nights = days - 1
    return { days, nights }
  }, [startDate, endDate])

  // Handlers for destination changes
  const handleLocationChange = (destinationIndex: number, value: string) => {
    setValue(`perDiem.perDiemDestinations.${destinationIndex}.locationName`, value, {
      shouldDirty: true,
    })
  }

  const handleLocationSelect = (
    destinationIndex: number,
    result: { value: string; label: string },
    locationData: LocationData | null
  ) => {
    // Update location name
    setValue(`perDiem.perDiemDestinations.${destinationIndex}.locationName`, result.label, {
      shouldDirty: true,
    })

    // Update location data from place details
    if (locationData) {
      setValue(`perDiem.perDiemDestinations.${destinationIndex}.country`, locationData.country, {
        shouldDirty: true,
      })
      setValue(
        `perDiem.perDiemDestinations.${destinationIndex}.administrativeAreaLevel1`,
        locationData.administrativeAreaLevel1 || null,
        { shouldDirty: true }
      )
      setValue(
        `perDiem.perDiemDestinations.${destinationIndex}.administrativeAreaLevel2`,
        locationData.administrativeAreaLevel2 || null,
        { shouldDirty: true }
      )
      setValue(
        `perDiem.perDiemDestinations.${destinationIndex}.locality`,
        locationData.locality || null,
        { shouldDirty: true }
      )
    }
  }

  const handleDestinationDateChange = (destinationIndex: number, dateString: string) => {
    if (!dateString) return

    const destination = destinations[destinationIndex]
    if (!destination) return

    const currentArrivalDate = destination.arrivalDate
    const newDate = stringToDate(dateString, 'ui')

    if (currentArrivalDate) {
      newDate.set({
        hours: moment(currentArrivalDate).hours(),
        minutes: moment(currentArrivalDate).minutes(),
      })
    } else {
      newDate.set({ hours: 9, minutes: 0 })
    }

    setValue(`perDiem.perDiemDestinations.${destinationIndex}.arrivalDate`, newDate.toISOString(), {
      shouldDirty: true,
    })
  }

  const handleDestinationTimeChange = (destinationIndex: number, timeString?: string) => {
    if (!timeString) return

    const destination = destinations[destinationIndex]
    if (!destination) return

    const currentArrivalDate = destination.arrivalDate
    const minDate =
      destinationIndex === 0
        ? startDate
        : destinations[destinationIndex - 1]?.arrivalDate || startDate

    const [hours, minutes] = timeString.split(':').map(Number)
    const baseDate = currentArrivalDate
      ? moment(currentArrivalDate)
      : minDate
      ? moment(minDate)
      : moment()
    baseDate.set({ hours, minutes })

    setValue(
      `perDiem.perDiemDestinations.${destinationIndex}.arrivalDate`,
      baseDate.toISOString(),
      {
        shouldDirty: true,
      }
    )
  }

  if (!destinations || destinations.length === 0) {
    return null
  }

  return (
    <FormBlock
      title={i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.routes_and_dates')}
      subtitle={i18n.t(
        'expenses_v2.forms.germany_per_diem_modal.trip_details.routes_and_dates_description'
      )}
    >
      <Box flexDirection="column" gap="s16">
        <DateTimeField
          label={i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.start_of_trip')}
          date={startDate}
          fieldName="perDiem.startDate"
          helperText={i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.start_helper')}
        />

        <VerticalConnector />

        {destinations.map((destination: (typeof destinations)[number], index: number) => {
          // First destination doesn't show arrival date
          const isFirstDestination = index === 0
          const showArrivalDate = !isFirstDestination
          // Min date is previous destination's arrival or start date
          const minDate =
            index === 0 ? startDate : destinations[index - 1]?.arrivalDate || startDate

          return (
            <React.Fragment key={destination.id || `destination-${index}`}>
              <DestinationItem
                locationName={destination.locationName}
                arrivalDate={destination.arrivalDate}
                showArrivalDate={showArrivalDate}
                onLocationChange={(value) => handleLocationChange(index, value)}
                onLocationSelect={(result, locationData) =>
                  handleLocationSelect(index, result, locationData)
                }
                onDateChange={(dateString) => handleDestinationDateChange(index, dateString)}
                onTimeChange={(timeString) => handleDestinationTimeChange(index, timeString)}
                minDate={minDate}
                placesLibraryLoaded={placesLibraryLoaded}
              />
              {index < destinations.length - 1 && <VerticalConnector />}
            </React.Fragment>
          )
        })}

        <VerticalConnector />

        <DateTimeField
          label={i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.end_of_trip')}
          date={endDate}
          fieldName="perDiem.endDate"
          helperText={i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.end_helper')}
          minDate={startDate}
        />

        {tripDuration && (
          <Box
            flexDirection="row"
            alignItems="center"
            gap="s8"
            padding="s12"
            background="viridian100"
            borderRadius={{ all: 'abs008' }}
          >
            <Icon.Small icon="info-circle" color="viridian1000" />
            <Text.Normal size="200" weight="medium" color="viridian1000">
              {i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.trip_summary', {
                summary: `${tripDuration.days} day${tripDuration.days !== 1 ? 's' : ''} · ${
                  tripDuration.nights
                } night${tripDuration.nights !== 1 ? 's' : ''}`,
              })}
            </Text.Normal>
          </Box>
        )}
      </Box>
    </FormBlock>
  )
}

export default Destinations
